import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  // Business Admin API functions
  getBusinessAdminOverview,
  getBusinessAdminSummary,
  getBusinessAdminCampaigns,
  getBusinessAdminAnalytics,
  getBusinessAdminBudgetAnalytics,
  getBusinessAdminCampaignDetail,
  getBusinessAdminFilterOptions,
  getBusinessAdminCampaignsByStatus,
  getBusinessAdminPendingApprovals,
  searchBusinessAdminCampaigns,
  approveCampaign as apiApproveCampaign,
  rejectCampaign as apiRejectCampaign,
  downloadCampaignPOCs as apiDownloadCampaignPOCs,
  updateCampaignStatus as apiUpdateCampaignStatus,
  editCampaignDetails as apiEditCampaignDetails,
  deleteCampaign as apiDeleteCampaign
} from '@/services/api';

// Status progression map
const STATUS_PROGRESSION = {
  'CREATED': 'IN PROGRESS',
  'IN PROGRESS': 'OUT FOR APPROVAL',
  'OUT FOR APPROVAL': 'LAUNCHED',
  'LAUNCHED': 'COMPLETED',
  'COMPLETED': null, // No next status
  'PAUSED': 'IN PROGRESS', // Can resume
};

/**
 * Comprehensive Business Admin Dashboard Hook
 * Handles all Business Admin Dashboard API endpoints
 */
export const useBusinessAdminDashboard = () => {
  const { toast } = useToast();
  
  // State Management
  const [overview, setOverview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [budgetAnalytics, setBudgetAnalytics] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Pagination State
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Loading and Error States
  const [isLoading, setIsLoading] = useState(false);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(false);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [isBudgetLoading, setIsBudgetLoading] = useState(false);
  const [error, setError] = useState(null);

  // Shared helper to extract campaigns + pagination from different result shapes
  const applyCampaignListResult = useCallback((result) => {
    if (!result?.status) {
      setCampaigns([]);
      return;
    }

    let campaignsData = [];

    if (result.data?.campaigns && Array.isArray(result.data.campaigns)) {
      campaignsData = result.data.campaigns;
    } else if (Array.isArray(result.data)) {
      campaignsData = result.data;
    }

    setCampaigns(campaignsData);

    if (result.data?.pagination) {
      setPagination(result.data.pagination);
    } else if (result.pagination) {
      setPagination(result.pagination);
    }
  }, []);

  /**
   * Helper function to handle API errors
   */
  const handleApiError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    setError({
      message: error.message || 'An error occurred',
      context,
      timestamp: new Date().toISOString()
    });
  }, []);

  /**
   * 1. Get Dashboard Overview
   * GET /api/business-admin/overview
   */
  const getDashboardOverview = useCallback(async (filters = {}) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getBusinessAdminOverview(filters);
      
      if (result.status) {
        setOverview(result.data);
        setSummary(result.data.summary);
        // Don't set campaigns here - they are fetched separately by getRecentCampaigns
        // setCampaigns(result.data.recent_campaigns?.campaigns || []);
        setAnalytics(result.data.analytics);
        
        // Don't set pagination here either - it's managed by getRecentCampaigns
        // if (result.data.recent_campaigns?.pagination) {
        //   setPagination(result.data.recent_campaigns.pagination);
        // }
      }

      return result;
    } catch (err) {
      handleApiError(err, 'getDashboardOverview');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  /**
   * 2. Get Dashboard Summary Metrics
   * GET /api/business-admin/summary
   */
  const getSummaryMetrics = useCallback(async (filters = {}) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getBusinessAdminSummary(filters);
      
      if (result.status) {
        setSummary(result.data);
      }

      return result;
    } catch (err) {
      handleApiError(err, 'getSummaryMetrics');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  /**
   * 3. Get Recent Campaigns (Ad Requests Table)
   * GET /api/business-admin/recent-campaigns
   */
  const getRecentCampaigns = useCallback(async (params = {}) => {
    try {
      setIsCampaignsLoading(true);
      setError(null);

      const result = await getBusinessAdminCampaigns(params);
      applyCampaignListResult(result);

      return result;
    } catch (err) {
      handleApiError(err, 'getRecentCampaigns');
      throw err;
    } finally {
      setIsCampaignsLoading(false);
    }
  }, [handleApiError, applyCampaignListResult]);

  /**
   * 4. Get Campaign Analytics
   * GET /api/business-admin/analytics
   */
  const getCampaignAnalytics = useCallback(async (filters = {}) => {
    try {
      setIsAnalyticsLoading(true);
      setError(null);

      const result = await getBusinessAdminAnalytics(filters);
      
      if (result.status) {
        setAnalytics(result.data);
      }

      return result;
    } catch (err) {
      handleApiError(err, 'getCampaignAnalytics');
      throw err;
    } finally {
      setIsAnalyticsLoading(false);
    }
  }, [handleApiError]);

  /**
   * 5. Get Budget Analytics
   * GET /api/business-admin/budget-analytics
   */
  const getBudgetAnalytics = useCallback(async (filters = {}) => {
    try {
      setIsBudgetLoading(true);
      setError(null);

      const result = await getBusinessAdminBudgetAnalytics(filters);
      
      if (result.status) {
        setBudgetAnalytics(result.data);
      }

      return result;
    } catch (err) {
      handleApiError(err, 'getBudgetAnalytics');
      throw err;
    } finally {
      setIsBudgetLoading(false);
    }
  }, [handleApiError]);

  /**
   * 6. Get Campaign Detail (Full Details)
   * GET /api/business-admin/campaign/{campaign_id}
   * 
   * Returns complete campaign information including:
   * - basic_info: Campaign metadata and status
   * - budget_info: Budget allocation and utilization
   * - poc_info: List of POCs with locations and budgets
   * - creative_info: Ad creative details
   * - targeting: Age range targeting
   * - media: Uploaded files (images/videos)
   */
  const getCampaignDetail = useCallback(async (campaignId) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getBusinessAdminCampaignDetail(campaignId);
      
      if (result.status && result.data) {
        // Store the complete campaign details with all sections:
        // - basic_info (campaign metadata)
        // - budget_info (budget allocation and utilization)
        // - poc_info (POCs with locations and budgets)
        // - creative_info (ad creative details)
        // - targeting (age range)
        // - media (uploaded files)
        setSelectedCampaign(result.data);
      }

      return result;
    } catch (err) {
      handleApiError(err, 'getCampaignDetail');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  /**
   * 7. Update Campaign Status
   * PUT /api/business-admin/campaign/{campaign_id}/status
   */
  const updateCampaignStatus = useCallback(async (campaignId, status, notes = '') => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await apiUpdateCampaignStatus(campaignId, status, notes);
      
      // Update the campaign in the local state if it exists
      if (result.status) {
        setCampaigns(prevCampaigns => 
          prevCampaigns.map(campaign => 
            campaign.campaign_id === campaignId 
              ? { ...campaign, status: status }
              : campaign
          )
        );

        if (selectedCampaign?.campaign_id === campaignId) {
          setSelectedCampaign(prev => ({ ...prev, status: status }));
        }
      }

      return result;
    } catch (err) {
      handleApiError(err, 'updateCampaignStatus');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError, selectedCampaign]);

  /**
   * Helper: Get next status in progression
   */
  const getNextStatus = useCallback((currentStatus) => {
    return STATUS_PROGRESSION[currentStatus] || null;
  }, []);

  /**
   * Helper: Move campaign to next status
   */
  const moveToNextStatus = useCallback(async (campaignId, currentStatus, notes = '') => {
    const nextStatus = getNextStatus(currentStatus);
    if (!nextStatus) {
      throw new Error('Campaign is already at final status or status is invalid');
    }
    return await updateCampaignStatus(campaignId, nextStatus, notes);
  }, [getNextStatus, updateCampaignStatus]);

  /**
   * 8. Approve Campaign
   * POST /api/business-admin/campaign/{campaign_id}/approve
   */
  const approveCampaign = useCallback(async (campaignId) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await apiApproveCampaign(campaignId);
      
      // Update the campaign status to LAUNCHED
      if (result.status) {
        setCampaigns(prevCampaigns => 
          prevCampaigns.map(campaign => 
            campaign.campaign_id === campaignId 
              ? { ...campaign, status: 'LAUNCHED' }
              : campaign
          )
        );

        if (selectedCampaign?.campaign_id === campaignId) {
          setSelectedCampaign(prev => ({ ...prev, status: 'LAUNCHED' }));
        }
      }

      return result;
    } catch (err) {
      handleApiError(err, 'approveCampaign');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError, selectedCampaign]);

  /**
   * 9. Reject Campaign
   * POST /api/business-admin/campaign/{campaign_id}/reject
   */
  const rejectCampaign = useCallback(async (campaignId, reason = '') => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await apiRejectCampaign(campaignId, reason);
      
      // Update the campaign status to IN PROGRESS
      if (result.status) {
        setCampaigns(prevCampaigns => 
          prevCampaigns.map(campaign => 
            campaign.campaign_id === campaignId 
              ? { ...campaign, status: 'IN PROGRESS' }
              : campaign
          )
        );

        if (selectedCampaign?.campaign_id === campaignId) {
          setSelectedCampaign(prev => ({ ...prev, status: 'IN PROGRESS' }));
        }
      }

      return result;
    } catch (err) {
      handleApiError(err, 'rejectCampaign');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError, selectedCampaign]);

  /**
   * 10. Delete Campaign
   * DELETE /api/business-admin/campaign/{campaign_id}
   * Handles response validation and shows appropriate toast notifications
   */
  const deleteCampaign = useCallback(async (campaignId) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await apiDeleteCampaign(campaignId);
      
      // Check backend response status
      if (result?.status === true || result?.status === 'success' || result?.success) {
        // Remove the campaign from the list
        setCampaigns(prevCampaigns => 
          prevCampaigns.filter(campaign => campaign.campaign_id !== campaignId)
        );

        // Clear selected campaign if it was the deleted one
        if (selectedCampaign?.campaign_id === campaignId) {
          setSelectedCampaign(null);
        }

        // Update pagination total
        setPagination(prev => ({
          ...prev,
          total: Math.max(0, prev.total - 1)
        }));

        // Show success toast with backend message
        toast({
          title: "Success",
          description: result?.message || "Campaign deleted successfully",
          variant: "default",
        });

        return result;
      } else {
        // Backend returned failure status
        const errorMsg = result?.message || "Failed to delete campaign";
        toast({
          title: "Delete Failed",
          description: errorMsg,
          variant: "destructive",
        });
        throw new Error(errorMsg);
      }
    } catch (err) {
      // Parse error message from backend
      let errorMessage = "Failed to delete campaign. Please try again.";
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Check if it's the specific CREATED status restriction error
      if (errorMessage.includes("CREATED") || errorMessage.includes("Only campaigns")) {
        toast({
          title: "Cannot Delete Campaign",
          description: "Only campaigns in draft status can be deleted. This campaign cannot be deleted.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
      
      handleApiError(err, 'deleteCampaign');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [selectedCampaign, handleApiError, toast]);

  /**
   * 11. Search Campaigns
   * GET /api/business-admin/search
   */
  const searchCampaigns = useCallback(async (searchParams = {}) => {
    try {
      setIsCampaignsLoading(true);
      setError(null);

      const result = await searchBusinessAdminCampaigns(searchParams);
      applyCampaignListResult(result);

      return result;
    } catch (err) {
      handleApiError(err, 'searchCampaigns');
      throw err;
    } finally {
      setIsCampaignsLoading(false);
    }
  }, [handleApiError, applyCampaignListResult]);

  /**
   * 11. Get Filter Options
   * GET /api/business-admin/filter-options
   */
  const getFilterOptions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getBusinessAdminFilterOptions();
      
      if (result.status) {
        setFilterOptions(result.data);
      }

      return result;
    } catch (err) {
      handleApiError(err, 'getFilterOptions');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  /**
   * 12. Get Campaigns by Status
   * GET /api/business-admin/campaigns-by-status/{status_value}
   */
  const getCampaignsByStatus = useCallback(async (statusValue, params = {}) => {
    try {
      setIsCampaignsLoading(true);
      setError(null);

      const result = await getBusinessAdminCampaignsByStatus(statusValue, params);
      applyCampaignListResult(result);

      return result;
    } catch (err) {
      handleApiError(err, 'getCampaignsByStatus');
      throw err;
    } finally {
      setIsCampaignsLoading(false);
    }
  }, [handleApiError, applyCampaignListResult]);

  /**
   * 13. Get Pending Approvals
   * GET /api/business-admin/pending-approvals
   */
  const getPendingApprovals = useCallback(async (params = {}) => {
    try {
      setIsCampaignsLoading(true);
      setError(null);

      const result = await getBusinessAdminPendingApprovals(params);
      applyCampaignListResult(result);

      return result;
    } catch (err) {
      handleApiError(err, 'getPendingApprovals');
      throw err;
    } finally {
      setIsCampaignsLoading(false);
    }
  }, [handleApiError, applyCampaignListResult]);

  /**
   * Helper: Reset Error State
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Helper: Reset All State
   */
  const resetState = useCallback(() => {
    setOverview(null);
    setSummary(null);
    setCampaigns([]);
    setAnalytics(null);
    setBudgetAnalytics(null);
    setFilterOptions(null);
    setSelectedCampaign(null);
    setPagination({
      page: 1,
      per_page: 10,
      total: 0,
      pages: 0,
      has_next: false,
      has_prev: false
    });
    setError(null);
  }, []);

  /**
   * Status Display Helper
   * Maps backend status to UI display format
   */
  const getStatusDisplay = useCallback((status) => {
    const statusMap = {
      'LAUNCHED': { display: 'PUBLISHED', color: 'green' },
      'OUT FOR APPROVAL': { display: 'PENDING APPROVAL', color: 'orange' },
      'COMPLETED': { display: 'COMPLETED', color: 'gray' },
      'CREATED': { display: 'DRAFT', color: 'blue' },
      'IN PROGRESS': { display: 'IN PROGRESS', color: 'blue' },
      'PAUSED': { display: 'PAUSED', color: 'red' }
    };
    
    return statusMap[status] || { display: status, color: 'gray' };
  }, []);

  /**
   * Campaign Detail Section Helpers
   * Extract specific sections from selectedCampaign for easier access
   */
  const getCampaignBasicInfo = useCallback(() => {
    return selectedCampaign?.basic_info || null;
  }, [selectedCampaign]);

  const getCampaignBudgetInfo = useCallback(() => {
    return selectedCampaign?.budget_info || null;
  }, [selectedCampaign]);

  const getCampaignPOCInfo = useCallback(() => {
    return selectedCampaign?.poc_info || null;
  }, [selectedCampaign]);

  const getCampaignCreativeInfo = useCallback(() => {
    return selectedCampaign?.creative_info || null;
  }, [selectedCampaign]);

  const getCampaignTargeting = useCallback(() => {
    return selectedCampaign?.targeting || null;
  }, [selectedCampaign]);

  const getCampaignMedia = useCallback(() => {
    return selectedCampaign?.media || null;
  }, [selectedCampaign]);

  /**
   * Edit Campaign Name
   * Edits the campaign name using the business admin edit endpoint
   * Handles response validation and shows appropriate toast notifications
   */
  const editCampaignName = useCallback(async (campaignId, newCampaignName) => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate inputs
      if (!campaignId) {
        const errorMsg = 'Campaign ID is required';
        toast({
          title: "Validation Error",
          description: errorMsg,
          variant: "destructive",
        });
        throw new Error(errorMsg);
      }
      if (!newCampaignName || !newCampaignName.trim()) {
        const errorMsg = 'Campaign name cannot be empty';
        toast({
          title: "Validation Error",
          description: errorMsg,
          variant: "destructive",
        });
        throw new Error(errorMsg);
      }

      // Call API to edit campaign name
      const result = await apiEditCampaignDetails(campaignId, {
        campaign_name: newCampaignName.trim()
      });

      // Check backend response status
      if (result?.status === true || result?.status === 'success') {
        // Update local state if this is the selected campaign
        if (selectedCampaign && selectedCampaign.campaign_id === campaignId) {
          setSelectedCampaign({
            ...selectedCampaign,
            campaign_name: newCampaignName.trim()
          });
        }

        // Update campaigns list if the campaign exists in it
        setCampaigns(prevCampaigns => 
          prevCampaigns.map(campaign => 
            campaign.campaign_id === campaignId
              ? { ...campaign, campaign_name: newCampaignName.trim() }
              : campaign
          )
        );

        // Show success toast with backend message
        toast({
          title: "Success",
          description: result?.message || "Campaign name updated successfully",
          variant: "default",
        });

        return result;
      } else {
        // Backend returned failure status
        const errorMsg = result?.message || "Failed to update campaign name";
        toast({
          title: "Update Failed",
          description: errorMsg,
          variant: "destructive",
        });
        throw new Error(errorMsg);
      }
    } catch (err) {
      // Parse error message from backend
      let errorMessage = "Failed to update campaign name. Please try again.";
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Check if it's the specific LAUNCHED/COMPLETED error
      if (errorMessage.includes("LAUNCHED") || errorMessage.includes("COMPLETED")) {
        toast({
          title: "Cannot Edit Campaign",
          description: "This campaign has been launched or completed and cannot be edited.",
          variant: "destructive",
        });
      } else if (!err.message || (err.message !== 'Campaign ID is required' && err.message !== 'Campaign name cannot be empty')) {
        // Only show toast if it wasn't already shown by validation
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
      
      handleApiError(err, 'editCampaignName');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [selectedCampaign, handleApiError, toast]);

  // Return all state and functions
  return {
    // State
    overview,
    summary,
    campaigns,
    analytics,
    budgetAnalytics,
    filterOptions,
    selectedCampaign,
    pagination,
    
    // Loading States
    isLoading,
    isCampaignsLoading,
    isAnalyticsLoading,
    isBudgetLoading,
    error,
    
    // API Functions
    getDashboardOverview,
    getSummaryMetrics,
    getRecentCampaigns,
    getCampaignAnalytics,
    getBudgetAnalytics,
    getCampaignDetail,
    updateCampaignStatus,
    moveToNextStatus,
    getNextStatus,
    approveCampaign,
    rejectCampaign,
    deleteCampaign,
    searchCampaigns,
    getFilterOptions,
    getCampaignsByStatus,
    getPendingApprovals,
    editCampaignName,
    
    // Helpers
    clearError,
    resetState,
    getStatusDisplay,
    
    // Campaign Detail Section Helpers
    getCampaignBasicInfo,
    getCampaignBudgetInfo,
    getCampaignPOCInfo,
    getCampaignCreativeInfo,
    getCampaignTargeting,
    getCampaignMedia,
    
    // State Setters (for manual updates if needed)
    setOverview,
    setSummary,
    setCampaigns,
    setAnalytics,
    setBudgetAnalytics,
    setFilterOptions,
    setSelectedCampaign,
    setPagination
  };
};

export default useBusinessAdminDashboard;
